/// <reference path="./i18next.d.ts" />
/**
 * The landing page's and control plane's i18next instance (doc/20261023-i18n-zh-cn.md §3.1).
 *
 * English is bundled and initialised synchronously. Each other language is a chunk loaded on first use, together
 * with its antd locale. A missing key falls back to English, never to the raw key.
 */
import i18next, { type Resource, type ResourceLanguage } from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Locale } from 'antd/es/locale';
import enUS from 'antd/locale/en_US';
import {
  DEFAULT_DISPLAY_LANGUAGE,
  isSupportedDisplayLanguage,
  type DisplayLanguageId,
} from '../../../shared/locale/locale.mjs';
import { escapeContent, unescapeContent } from '../../../shared/locale/content-escape.mjs';

type CatalogueModule = { readonly default: ResourceLanguage[string] };

const englishModules = import.meta.glob<CatalogueModule>('../locales/en/*.json', { eager: true });
const lazyModules = import.meta.glob<CatalogueModule>(['../locales/*/*.json', '!../locales/en/*.json']);

function namespaceOf(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1, -'.json'.length);
}

function languageOf(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 2]!;
}

const english: ResourceLanguage = Object.fromEntries(
  Object.entries(englishModules).map(([path, module]) => [namespaceOf(path), module.default]),
);

const resources: Resource = { [DEFAULT_DISPLAY_LANGUAGE]: english };

void i18next.use(initReactI18next).init({
  resources,
  lng: DEFAULT_DISPLAY_LANGUAGE,
  fallbackLng: DEFAULT_DISPLAY_LANGUAGE,
  supportedLngs: ['en', 'zh-Hans-CN'],
  // Never look for `zh` or `zh-Hans` bundles: a language is exactly one catalogue.
  load: 'currentOnly',
  nonExplicitSupportedLngs: false,
  ns: Object.keys(english),
  defaultNS: 'common',
  initAsync: false,
  returnNull: false,
  returnEmptyString: false,
  // t() inserts values as they are (React escapes text). Only the content-safe Trans asks for escaping
  // (i18n/Trans.tsx); its escape and unescape keep content opaque through react-i18next's second pass.
  interpolation: { escapeValue: false, escape: escapeContent },
  react: { useSuspense: false, unescape: unescapeContent },
});

export const i18n = i18next;

const antdLocales = new Map<DisplayLanguageId, Locale>([['en', enUS]]);

const antdLoaders: Record<Exclude<DisplayLanguageId, 'en'>, () => Promise<Locale>> = {
  'zh-Hans-CN': async () => (await import('antd/locale/zh_CN')).default,
};

/** Loads a language's catalogues and antd locale. Resolves once it can be shown. */
export async function loadDisplayLanguage(language: DisplayLanguageId): Promise<void> {
  if (antdLocales.has(language)) return;
  const entries = Object.entries(lazyModules).filter(([path]) => languageOf(path) === language);
  const [catalogues, antd] = await Promise.all([
    Promise.all(entries.map(async ([path, load]) => [namespaceOf(path), (await load()).default] as const)),
    antdLoaders[language as Exclude<DisplayLanguageId, 'en'>](),
  ]);
  for (const [namespace, catalogue] of catalogues) i18next.addResourceBundle(language, namespace, catalogue, true, true);
  antdLocales.set(language, antd);
}

export function antdLocaleFor(language: DisplayLanguageId): Locale {
  return antdLocales.get(language) ?? enUS;
}

/** The active display language, for code outside React. */
export function currentDisplayLanguage(): DisplayLanguageId {
  return isSupportedDisplayLanguage(i18next.language) ? i18next.language : DEFAULT_DISPLAY_LANGUAGE;
}

/** Shows `language`: loads it if needed, then switches i18next and `<html lang>` together. */
export async function applyDisplayLanguage(language: DisplayLanguageId): Promise<void> {
  await loadDisplayLanguage(language);
  await i18next.changeLanguage(language);
  document.documentElement.lang = language;
}
